import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Sliders, RefreshCw, Download, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
    computeKPIs, 
    detectAnomalies, 
    exportReport, 
    generateDemoData 
} from '../lib/api';

export const Settings = () => {
    const [windowSize, setWindowSize] = useState(30);
    const [zThreshold, setZThreshold] = useState(2.0);
    const [computing, setComputing] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [generating, setGenerating] = useState(false);

    const handleComputeKPIs = async () => {
        try {
            setComputing(true);
            const result = await computeKPIs();
            toast.success(`Computed KPIs for ${result.data.days_computed} days`);
        } catch (error) {
            console.error('Failed to compute KPIs:', error);
            toast.error('Failed to compute KPIs');
        } finally {
            setComputing(false);
        }
    };

    const handleDetectAnomalies = async () => {
        try {
            setDetecting(true);
            const result = await detectAnomalies({
                window_size: windowSize,
                z_threshold: zThreshold
            });
            toast.success(`Detected ${result.data.anomalies_found} anomalies`);
        } catch (error) {
            console.error('Failed to detect anomalies:', error);
            toast.error('Failed to detect anomalies');
        } finally {
            setDetecting(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await exportReport();
            const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kpi-report-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Report exported successfully!');
        } catch (error) {
            console.error('Failed to export report:', error);
            toast.error('Failed to export report');
        }
    };

    const handleGenerateDemo = async () => {
        try {
            setGenerating(true);
            toast.info('Generating demo data...', { duration: 10000 });
            await generateDemoData(90);
            toast.success('Demo data generated successfully!');
        } catch (error) {
            console.error('Failed to generate demo data:', error);
            toast.error('Failed to generate demo data');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-card-foreground flex items-center gap-3">
                    <SettingsIcon className="h-8 w-8 text-primary" />
                    Settings
                </h1>
                <p className="text-muted-foreground mt-1">
                    Configure anomaly detection and manage data
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Anomaly Detection Settings */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Sliders className="h-5 w-5 text-primary" />
                            Anomaly Detection
                        </CardTitle>
                        <CardDescription>
                            Configure the sensitivity of anomaly detection
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Rolling Window Size</Label>
                                    <span className="text-sm font-mono text-muted-foreground">{windowSize} days</span>
                                </div>
                                <Slider
                                    value={[windowSize]}
                                    onValueChange={(v) => setWindowSize(v[0])}
                                    min={7}
                                    max={90}
                                    step={1}
                                    className="cursor-pointer"
                                    data-testid="window-size-slider"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Number of historical days used to calculate baseline
                                </p>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Z-Score Threshold</Label>
                                    <span className="text-sm font-mono text-muted-foreground">{zThreshold.toFixed(1)}σ</span>
                                </div>
                                <Slider
                                    value={[zThreshold]}
                                    onValueChange={(v) => setZThreshold(v[0])}
                                    min={1.5}
                                    max={4.0}
                                    step={0.1}
                                    className="cursor-pointer"
                                    data-testid="z-threshold-slider"
                                />
                                <p className="text-xs text-muted-foreground">
                                    Standard deviations from mean to flag as anomaly (lower = more sensitive)
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button 
                                onClick={handleComputeKPIs}
                                disabled={computing}
                                variant="outline"
                                className="flex-1"
                                data-testid="compute-kpis-btn"
                            >
                                {computing ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                Recompute KPIs
                            </Button>
                            <Button 
                                onClick={handleDetectAnomalies}
                                disabled={detecting}
                                className="flex-1 btn-glow"
                                data-testid="detect-anomalies-btn"
                            >
                                {detecting ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Sliders className="h-4 w-4 mr-2" />
                                )}
                                Run Detection
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Data Management */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Download className="h-5 w-5 text-primary" />
                            Data Management
                        </CardTitle>
                        <CardDescription>
                            Export reports and manage demo data
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            <div className="p-4 rounded-lg bg-background/50 border border-border">
                                <h4 className="font-medium text-card-foreground mb-2">Export Report</h4>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Download a JSON report containing all KPIs, anomalies, and alerts
                                </p>
                                <Button 
                                    variant="outline"
                                    onClick={handleExport}
                                    className="w-full"
                                    data-testid="export-report-btn"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export JSON Report
                                </Button>
                            </div>

                            <div className="p-4 rounded-lg bg-background/50 border border-border">
                                <h4 className="font-medium text-card-foreground mb-2">Demo Data</h4>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Generate a simulated ecommerce dataset with intentional anomalies
                                </p>
                                <Button 
                                    onClick={handleGenerateDemo}
                                    disabled={generating}
                                    className="w-full btn-glow"
                                    data-testid="generate-demo-settings-btn"
                                >
                                    {generating ? (
                                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Sparkles className="h-4 w-4 mr-2" />
                                    )}
                                    {generating ? 'Generating...' : 'Generate Demo Data'}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info Card */}
            <Card className="bg-card border-border">
                <CardContent className="p-6">
                    <h4 className="font-semibold text-card-foreground mb-3">About Anomaly Detection</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        <div>
                            <p className="font-medium text-primary mb-1">Z-Score Method</p>
                            <p className="text-muted-foreground">
                                Measures how many standard deviations a value is from the rolling mean. Values beyond the threshold are flagged as anomalies.
                            </p>
                        </div>
                        <div>
                            <p className="font-medium text-primary mb-1">Severity Levels</p>
                            <p className="text-muted-foreground">
                                Critical (≥4σ), High (≥3σ), Medium (≥2.5σ), Low (≥threshold). Higher z-scores indicate more significant deviations.
                            </p>
                        </div>
                        <div>
                            <p className="font-medium text-primary mb-1">Driver Analysis</p>
                            <p className="text-muted-foreground">
                                For each anomaly, the system breaks down contribution by category, region, and channel to identify root causes.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Settings;
